/**
 * Role → permission map (CFG-006). Invariants enforced by tests/rbac.test.ts:
 *  - Administrators (super_admin, org_admin) hold no clinical-data permissions.
 *  - Only doctor and radiologist may sign or amend reports.
 *  - Anyone who can review AI output can also read it.
 *  - Patients hold only `*_self` permissions.
 *  - Only super_admin configures the system, creates organizations, or manages AI models.
 */
export const ROLE_PERMISSIONS = {
    super_admin: [
        "system:configure",
        "org:create",
        "org:read",
        "org:update",
        "facility:create",
        "facility:read",
        "facility:update",
        "user:invite",
        "user:read",
        "user:update",
        "user:disable",
        "role:assign",
        "audit:read",
        "ai:model_manage",
    ],
    org_admin: [
        "org:read",
        "org:update",
        "facility:create",
        "facility:read",
        "facility:update",
        "user:invite",
        "user:read",
        "user:update",
        "user:disable",
        "role:assign",
        "audit:read",
    ],
    doctor: [
        "patient:create",
        "patient:read",
        "patient:update",
        "appointment:manage",
        "encounter:create",
        "encounter:read",
        "encounter:update",
        "imaging:read",
        "lab:create",
        "lab:read",
        "ai:request",
        "ai:read",
        "ai:review",
        "report:draft",
        "report:read",
        "report:sign",
        "report:amend",
    ],
    radiologist: [
        "patient:read",
        "encounter:read",
        "imaging:upload",
        "imaging:read",
        "lab:read",
        "dataset:create",
        "dataset:read",
        "dataset:annotate",
        "dataset:publish",
        "ai:request",
        "ai:read",
        "ai:review",
        "report:draft",
        "report:read",
        "report:sign",
        "report:amend",
    ],
    technician: ["patient:read", "imaging:upload", "imaging:read", "ai:request"],
    receptionist: ["patient:create", "patient:read", "patient:update", "appointment:manage"],
    ai_reviewer: ["imaging:read", "dataset:read", "ai:read", "ai:review"],
    patient: ["patient:read_self", "report:read_self"],
};
/** Union of permissions granted by the given roles. */
export function permissionsForRoles(roles) {
    const granted = new Set();
    for (const role of roles) {
        for (const permission of ROLE_PERMISSIONS[role])
            granted.add(permission);
    }
    return granted;
}
export function hasPermission(roles, permission) {
    return permissionsForRoles(roles).has(permission);
}
export function hasAllPermissions(roles, required) {
    const granted = permissionsForRoles(roles);
    return required.every((p) => granted.has(p));
}
//# sourceMappingURL=role-permissions.js.map